import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';

import * as _ from 'lodash';
import moment from 'moment';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';

import * as Papi from '@app/papi';
import { StorageService } from '../_services';

// Api
import { Auth, User } from 'papi-client-ng2';

// Environment
import { environment } from '../../../environments/environment';

export interface AppliationClient {
	id: string;
	name: string;
}

export interface Token {
	access_token?: string;
	created?: moment.Moment;
	expires_in?: string | number;
	id_token?: string;
	refresh_token?: string;
	token_type?: string;
}

export interface AuthResponse {
	appName?: string;
	autoConfirmUser?: boolean;
	refreshExpiresIn?: string | number;
	applicationClient?: AppliationClient[]; 
	applicationClientId?: string;
	tenantId?: string | number;
	userPoolId?: string;
	autoVerifyEmail?: boolean;
	autoVerifyPhone?: boolean;
	domain?: string;
	token?: Token;
}

@Injectable()
export class CognitoService {
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private http: HttpClient,
		private papi: Papi.Service,
		private storage: StorageService,
		@Inject(DOCUMENT) private document: any
	) {
		this.storage.init(environment?.authkeys?.PAA_COGNITO_TOKEN);		// see if we can initialize off of a stored value
	}

	public static getApplicationClientId(cognito: AuthResponse): string {
		const appName: string = cognito.appName || environment.cognito.appName;

		return (_.find(cognito.applicationClient || [], {name: appName}) || {id: null}).id;
	}

	public get instance(): AuthResponse {
		return <AuthResponse>JSON.parse(this.storage.instance.getItem(environment?.authkeys?.PAA_COGNITO_TOKEN));
	}

	public getRedirectResult(): string {
		const error: string = this.route.snapshot.queryParams['error_description'];		// cognito auth failed with this error

		if (!_.isEmpty(error)) {
			throw new HttpErrorResponse({ error: new Error(error), status: 500, statusText: 'Internal server error', url: this.router.url });
		}
		return this.route.snapshot.queryParams['code'];
	}

	public logout() {
		// this.papi.logout();
		this.storage.instance.removeItem(environment?.authkeys?.PAA_COGNITO_TOKEN);
	}

	public query(email: string): Observable<AuthResponse> {
		const domain: string = (email || '').replace(/^[^@]+@(.*)$/, '$1');		// extract domain from email addy
		const endpoint = environment.cognito.lookupDomain + '/domain?name=' + domain;

		if (!domain) {
			this.storage.instance.removeItem(environment?.authkeys?.PAA_COGNITO_TOKEN);
			return of(<AuthResponse>{});
		}
		return this.http.get(endpoint).pipe(mergeMap((data: any) => {
			if (_.isEmpty(data) || _.isEmpty(data.Item)) {
				this.storage.instance.removeItem(environment?.authkeys?.PAA_COGNITO_TOKEN);
				return of(<AuthResponse>{});
			}
			data.Item.appName = environment.cognito.appName;
			this.storage.instance.setItem(environment?.authkeys?.PAA_COGNITO_TOKEN, JSON.stringify(data.Item));
			return of(<AuthResponse>(data.Item));
		}));
	}

	public redirectForCode(data: AuthResponse) {
		// let target: string = '';

		// target += environment.cognito.authDomain;
		// target += '/login?response_type=code&client_id=';
		// target += CognitoService.getApplicationClientId(data);
		// target += '&redirect_uri=';
		// target += environment.cognito.redirectUri;
		// this.document.location.href = target;		// @TODO - find better mechanism to handle the redirect
	}

	public refresh(): Observable<Auth> {
		const cognitoAuthResponse: AuthResponse = <AuthResponse>(JSON.parse(this.storage.instance.getItem(environment?.authkeys?.PAA_COGNITO_TOKEN)) || {});
		const clientid: string = CognitoService.getApplicationClientId(cognitoAuthResponse);
		const data: any = {
			UserPoolId: cognitoAuthResponse.userPoolId,
			ClientId: clientid
		};
		const pool: any = new CognitoUserPool(data);
		const target: string = (<any>pool).client.endpoint;
		const body: any = {
			ClientId: clientid,
			AuthFlow: 'REFRESH_TOKEN_AUTH',
			AuthParameters: {
				REFRESH_TOKEN: cognitoAuthResponse.token.refresh_token
			}
		};
		const options: any = {
			headers: {
				'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
				'Content-Type': 'application/x-amz-json-1.1'
			},
			responseType: 'text',
			withCredentials: false
		};

		return this.http.post(target, JSON.stringify(body), options).pipe(
			mergeMap((result: string | ArrayBuffer) => {
				const data: any = JSON.parse(result.toString()).AuthenticationResult;
				const token: Token = cognitoAuthResponse.token;

				token.access_token = data.AccessToken;
				token.id_token = data.IdToken;
				token.expires_in = data.ExpiresIn;
				token.created = moment().utc();
				this.storage.instance.setItem(environment?.authkeys?.PAA_COGNITO_TOKEN, JSON.stringify(cognitoAuthResponse));
				return this.papi.tokenLogin(token);		// @TODO - MOVE THIS LOGIC TO CALLER AND OUT OF COGNITO SERVCIE
			})
		);
	}

	//public signin(code: string): Observable<Auth> {
		// const cognitoAuthResponse: AuthResponse = <AuthResponse>(JSON.parse(this.storage.instance.getItem(environment.authkeys.PAA_COGNITO_TOKEN)) || {});
		// const target: string = environment.cognito.authDomain + '/oauth2/token';
		// const body: any = {};
		// const options: any = {
		// 	headers: {
		// 		'Content-Type': 'application/x-www-form-urlencoded'
		// 	},
		// 	params: {
		// 		'grant_type': 'authorization_code',
		// 		'code': code,
		// 		'client_id': CognitoService.getApplicationClientId(cognitoAuthResponse),
		// 		'redirect_uri': environment.cognito.redirectUri
		// 	},
		// 	responseType: 'text'
		// };

		// if (_.isEmpty(cognitoAuthResponse)) {
		// 	return throwError(new HttpErrorResponse({ error: new Error('Not Found'), status: 404, statusText: 'Not Found', url: this.router.url }));
		// }
		// return this.http.post(target, body, options).pipe(
		// 	mergeMap((result: string | ArrayBuffer) => {
		// 		const data: Token = <Token>JSON.parse(result.toString());

		// 		data.created = moment().utc();		// convert toISOString() where appropriate
		// 		cognitoAuthResponse.token = data;
		// 		this.storage.instance.setItem(environment.authkeys.PAA_COGNITO_TOKEN, JSON.stringify(cognitoAuthResponse));
		// 		return this.papi.tokenLogin(data);
		// 	}),
		// 	catchError((error: Error) => {
		// 		this.storage.instance.removeItem(environment.authkeys.PAA_COGNITO_TOKEN);
		// 		throw error;
		// 	})
		// );
	//}
}
